import { useCallback, useEffect, useState } from 'react';
import PageHeader from '../../components/layout/PageHeader';
import { tokens } from '../../components/forms/tokens';
import { getApiErrorMessage } from '../../lib/apiError';
import { cleanMfaInput, isMfaCode, stepUpErrorMessage } from '../../lib/mfaApproval';
import { readerLocale } from '../../i18n/language';
import adminJurisdictionsService, { type Country } from '../../services/adminJurisdictions.service';
import { countryAction, reasonProblem, stateLabel } from './adminJurisdictions.helpers';

/**
 * Super admin: the country switch. A country is where the portal may do NEW business. One super
 * admin turns it on, with a reason and an authentication code, and off, with a reason. A product is offered in a
 * country only while that country is on (and the product itself can be operated). Turning a
 * country off stops new business only: money already proposed or received, withdrawals,
 * balances and statements are not touched.
 */
export default function AdminJurisdictions(): JSX.Element {
  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // The notice belongs to the country that changed, shown on its own card: a line at the top of a long
  // page was out of sight of the button that caused it.
  const [flash, setFlash] = useState<{ code: string; message: string } | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      setCountries(await adminJurisdictionsService.list());
    } catch (e) {
      setErr(getApiErrorMessage(e, 'Could not load the countries.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  return (
    <div style={{ padding: 24, color: tokens.textPrimary }}>
      <PageHeader
        title="Countries"
        subtitle="The portal does new business only in a country that is enabled. A super admin turns one on with a reason and an authentication code, and off with a reason. Turning one off stops new business only."
      />
      {err ? <div style={errBox}>{err}</div> : null}
      {loading && countries.length === 0 ? <p style={noteText}>Loading…</p> : null}
      <div style={{ display: 'grid', gap: 16, maxWidth: 900 }}>
        {countries.map((c) => (
          <CountryCard
            key={c.code}
            country={c}
            notice={flash?.code === c.code ? flash.message : null}
            onDone={async (message) => {
              setFlash({ code: c.code, message });
              await reload();
            }}
          />
        ))}
      </div>
    </div>
  );
}

function CountryCard({
  country,
  notice,
  onDone,
}: Readonly<{
  country: Country;
  notice: string | null;
  onDone: (message: string) => Promise<void>;
}>): JSX.Element {
  const action = countryAction(country);
  return (
    <section style={card}>
      <div style={headerRow}>
        <h2 style={{ fontSize: 16, margin: 0 }}>{country.name}</h2>
        <span style={country.enabled ? tagOn : tag}>{stateLabel(country)}</span>
      </div>
      {notice ? <output style={okBox}>{notice}</output> : null}
      {action === 'none' ? <p style={noteText}>The portal does not operate in this country.</p> : null}
      {action === 'none' ? null : <SwitchPanel country={country} off={action === 'off'} onDone={onDone} />}
      {country.history.length > 0 ? (
        <details style={{ marginTop: 12 }}>
          <summary style={{ fontSize: 13, cursor: 'pointer' }}>History of the switch</summary>
          <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 12 }}>
            {country.history.map((e) => (
              <li key={`${e.at}-${e.state}`}>
                <strong>{historyLabel(e.state)}</strong>
                {` · ${new Date(e.at).toLocaleString(readerLocale())}`}
                {` · ${e.confirmed_by_name ?? e.requested_by_name ?? ''}`}
                {` · ${e.reason}`}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </section>
  );
}

function historyLabel(state: 'requested' | 'enabled' | 'disabled'): string {
  if (state === 'enabled') return 'Enabled';
  if (state === 'disabled') return 'Disabled';
  return 'Asked to enable';
}

function switchLabel(off: boolean): string {
  return off ? 'Switch off' : 'Switch on';
}

function SwitchPanel({
  country,
  off,
  onDone,
}: Readonly<{
  country: Country;
  off: boolean;
  onDone: (message: string) => Promise<void>;
}>): JSX.Element {
  const [reason, setReason] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    setBusy(true);
    setErr(null);
    try {
      if (off) {
        await adminJurisdictionsService.off(country.code, reason.trim());
        await onDone('Country switched off.');
      } else {
        await adminJurisdictionsService.on(country.code, reason.trim(), code);
        await onDone('Country switched on.');
      }
      setReason('');
      setCode('');
    } catch (e) {
      setErr(off ? getApiErrorMessage(e, 'Could not save.') : stepUpErrorMessage(e, 'Could not switch the country on.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 12, display: 'grid', gap: 8 }}>
      {err ? <p style={{ color: tokens.errorText, fontSize: 12 }}>{err}</p> : null}
      <label style={labelStyle}>
        <span>{off ? 'Reason for switching it off' : 'Reason for switching it on'}</span>
        <textarea
          value={reason}
          rows={2}
          maxLength={500}
          disabled={busy}
          onChange={(e) => setReason(e.target.value)}
          style={inputStyle}
        />
      </label>
      {off ? null : (
        <label style={labelStyle}>
          <span>Authentication code</span>
          <input
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            disabled={busy}
            onChange={(e) => setCode(cleanMfaInput(e.target.value))}
            style={{ ...inputStyle, width: 140 }}
          />
        </label>
      )}
      <div>
        <button
          type="button"
          disabled={busy || reasonProblem(reason) !== null || (!off && !isMfaCode(code))}
          onClick={() => void submit()}
          style={off ? btnSecondary : btnPrimary}
        >
          {busy ? 'Saving…' : switchLabel(off)}
        </button>
      </div>
    </div>
  );
}

const card: React.CSSProperties = {
  background: tokens.surface,
  border: `1px solid ${tokens.border}`,
  borderRadius: 12,
  padding: 16,
};
const headerRow: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
};
const tag: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  padding: '2px 10px',
  borderRadius: 999,
  background: tokens.surfaceMuted,
  color: tokens.textLabel,
};
const tagOn: React.CSSProperties = { ...tag, background: tokens.successBg, color: tokens.successText };
const noteText: React.CSSProperties = { fontSize: 12, color: tokens.textMuted, margin: '4px 0 0' };
const labelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  fontSize: 12,
  color: tokens.textLabel,
};
const inputStyle: React.CSSProperties = {
  background: tokens.surfaceMuted,
  border: `1px solid ${tokens.border}`,
  borderRadius: 8,
  padding: '8px 10px',
  color: tokens.textPrimary,
  fontFamily: 'inherit',
};
const btnPrimary: React.CSSProperties = {
  background: tokens.gold,
  color: tokens.navy,
  border: 'none',
  borderRadius: 8,
  padding: '8px 16px',
  fontWeight: 600,
  cursor: 'pointer',
};
const btnSecondary: React.CSSProperties = {
  background: 'transparent',
  color: tokens.textPrimary,
  border: `1px solid ${tokens.border}`,
  borderRadius: 8,
  padding: '8px 16px',
  fontWeight: 600,
  cursor: 'pointer',
};
const errBox: React.CSSProperties = {
  background: tokens.errorBg,
  border: `1px solid ${tokens.errorBorder}`,
  color: tokens.errorText,
  padding: 12,
  borderRadius: 8,
  marginBottom: 12,
  fontSize: 13,
};
const okBox: React.CSSProperties = {
  display: 'block',
  background: tokens.successBg,
  border: `1px solid ${tokens.successBorder}`,
  color: tokens.successText,
  padding: 12,
  borderRadius: 8,
  marginBottom: 12,
  fontSize: 13,
};
